<template>
    <div class="complete">
        <div v-if="reportDownloadUrl" class="report-download">
            <div class="report-download-code">
                <qrcode :value="reportDownloadUrl" :size="220" logo></qrcode>
            </div>
            <div class="report-download-text">
                Scan this QR Code to download the Auggie Target Validation Report.
            </div>
        </div>
        <header-message class="text-success">{{ message }}</header-message>
        <div class="buttonrow">
            <button-button class="btn btn-primary" type="submit" @click.prevent="acknowledgeClicked">Complete</button-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .complete {
        padding-top: 1.5rem;
        padding-bottom: 5rem;// the global .buttonrow is fixed to the bottom of the screen
    }
    .report-download {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .report-download-code {
        display: flex;// the QR renders as a canvas, which would otherwise leave a baseline gap below it
        background: white;
        padding: 0.5rem;
    }
    .report-download-text {
        margin-top: 0.75rem;
        text-align: center;
        font-size: 1.5rem;
    }
</style>

<script lang="ts">
import { defineComponent } from "vue";
import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";
import Qrcode from "@/components/Qrcode.vue";
import { Global } from "@/support/GlobalData";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { DataRequest } from "@/shared/support/Data";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";
import { ReportDownloadDto } from "@/shared/models/ReportDownloadDto";
import { SignalR } from "@/support/SignalR";
import { CalibrationType } from "@/shared/enums/CalibrationType";

export default defineComponent({
    name: "CalibrationComplete",
    components: {
        HeaderMessage,
        Qrcode,
    },
    mixins: [PageBase],
    data() {
        return {
            reportDownloadUrl: "",
        };
    },
    computed: {
        message(): string {
            if (Global.CalibrationSession?.CalibrationType === CalibrationType.Sequential)
                return "Targets have been successfully displayed.";
            else
                return "Target has been successfully displayed.";
        },
    },
    mounted(): void {
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onAcknowledgeByCompanion = this.acknowledgeClicked;
        SignalR.onCalibrationSuccess = (): void => {
            // no action (we sent it in successClicked), avoid out of sync popup
        };
        CalibrationEventSupport.sendBatteryInformation();// send final battery info
        this.loadReportDownload();// sends the page status once the download address is known
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onAcknowledgeByCompanion = null;
        SignalR.onCalibrationSuccess = null;
    },
    methods: {
        acknowledgeClicked(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationEnded",
            } as DeviceStatusDto);

            Global.MainApp.clearSession();
            this.$router.push("/");
        },
        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationComplete",
            } as DeviceStatusDto);
        },
        loadReportDownload(): void {
            if (Global.IsDemo() || !Global.CalibrationSession) {
                this.setReportDownloadUrl("");
                return;
            }

            // The address comes back empty when the device is not allowed to offer a report download.
            // A failure here must not disturb a calibration that has already succeeded, so it is silent,
            // and retries are off: the Companion is held on its previous page until this settles, and a
            // download code is not worth stalling it for.
            const dr = new DataRequest(undefined, undefined, 1);// a single attempt: DataRequest reads 0 as "use the app default"
            dr.autoToastOnFailure = false;
            dr.$post<null, ReportDownloadDto>("/Service/ReportDownloadToken/Issue", { CalibrationSessionGuid: Global.CalibrationSession.CalibrationSessionGuid })
                .then((download: ReportDownloadDto): void => {
                    if (!Global.CalibrationSession) return;// acknowledged while the request was in flight
                    this.setReportDownloadUrl(download?.Url || "");
                })
                .catch((): void => {
                    if (!Global.CalibrationSession) return;
                    this.setReportDownloadUrl("");
                });
        },
        setReportDownloadUrl(url: string): void {
            this.reportDownloadUrl = url;

            // The address is held globally so it rides the status payload on the way to the Companion,
            // including the CalibrationEnded status sent just before the session is cleared.
            Global.ReportDownloadUrl = url;

            // Every path through loadReportDownload ends here, so this is the single point at which the
            // Companion is told the device reached this page — and by then the address is settled.
            this.getCurrentStatusForListeners();
        },
    },
});
</script>
